/* ============================================================
   listaPratiche — le primitive comuni alle liste di pratiche.

   Esistevano una sola volta, dentro `RequestList.tsx` (la coda RDI), chiuse
   nella closure del componente: intestazione di colonna con ordinamento e
   popover di filtro, badge di causale e priorita', celle di impianto,
   confidenza, riferimento, allegati, data. La dashboard "Agente"
   (`ConversazioniList.tsx`) mostra le stesse pratiche — stessa tabella,
   stesso registro di stato, stessa pagina di dettaglio — e ne aveva una
   versione poverissima: canale, contatto, oggetto, due date. Nessun
   impianto, nessuna confidenza, nessuna causale, nessun filtro.

   La scelta e' estrarre qui la parte comune invece di ricopiarla: una
   seconda tabella diverge alla prima modifica. Cio' che resta specifico di
   una lista (quali colonne, quali filtri, da dove arrivano i dati) resta
   nella pagina; qui c'e' solo *come* si disegna una colonna e una cella.

   Regola di impaginazione ereditata dalle due liste: `tableLayout: fixed` +
   `minWidth` NUMERICO letterale sul <table> (mai `var(--space-*)` come
   larghezza — bug di design-token gia' ricomparso piu' volte, coperto da
   tests/test_frontend_design_tokens.py). */

import { useEffect, useState } from "react";
import { stripPkPrefix } from "../helpers/entityUtils";
import type { ProcessingRequest } from "../api/types";

/* ─── Opzioni dei filtri a scelta multipla ─── */

export const PRIORITY_OPTIONS = [
  { value: "", label: "Tutti" },
  { value: "alta", label: "Alta" },
  { value: "media", label: "Media" },
  { value: "bassa", label: "Bassa" },
];

export const CAUSALE_OPTIONS = [
  { value: "", label: "Tutte" },
  { value: "apertura", label: "Nuovo" },
  { value: "aggiornamento", label: "Aggiornamento" },
  { value: "escalation_ticket_esistente", label: "Escalation" },
  { value: "duplicato_allarme", label: "Duplicato allarme" },
];

/** Mini-config per il badge causale compatto in tabella. */
export const CAUSALE_BADGE: Record<string, { bg: string; color: string; icon: string; short: string; full: string }> = {
  apertura:                    { bg: "var(--indaco-success-light)", color: "var(--indaco-success)", icon: "add_circle",       short: "Nuovo",        full: "Nuovo ticket" },
  aggiornamento:               { bg: "var(--indaco-danger-light)",  color: "var(--indaco-danger)",  icon: "update",            short: "Aggiornam.",   full: "Aggiornamento ticket esistente" },
  escalation_ticket_esistente: { bg: "var(--indaco-warning-light)", color: "var(--indaco-warning)", icon: "warning",           short: "Escalation",   full: "Escalation: ticket esistente" },
  duplicato_allarme:           { bg: "var(--indaco-danger-light)",  color: "var(--indaco-danger)",  icon: "notifications_off", short: "Dup. allarme", full: "Duplicato allarme (ignorato)" },
};

/* ─── Lettura dei campi della pratica ─── */

function primaAnalisi(item: ProcessingRequest): Record<string, unknown> | null {
  const analyses = item.analyses;
  const a = Array.isArray(analyses) ? analyses[0] : analyses;
  return (a as Record<string, unknown> | null) ?? null;
}

export function getCausale(item: ProcessingRequest): string | null {
  const c = primaAnalisi(item)?.causale;
  return c ? String(c) : null;
}

export function getNumeroRiferimento(item: ProcessingRequest): string | null {
  const n = primaAnalisi(item)?.numero_riferimento;
  return n ? String(n) : null;
}

/** Il motivo scritto dall'analisi: per una pratica nata da conversazione e'
 *  l'unico testo che dice davvero di cosa si tratta (il subject e' sintetico). */
export function getMotivo(item: ProcessingRequest): string | null {
  const m = primaAnalisi(item)?.motivo;
  return m ? String(m) : null;
}

/** Priorita' normalizzata: la pipeline ha prodotto nel tempo anche `urgente`
 *  e `normale`, che qui diventano `alta` e `bassa`. */
export function getPriorita(item: ProcessingRequest): string {
  const raw = String(primaAnalisi(item)?.priorita ?? "").toLowerCase();
  return raw === "urgente" ? "alta" : raw === "normale" ? "bassa" : raw;
}

export function getOverallConfidence(item: ProcessingRequest): number | null {
  if (!item.matches || item.matches.length === 0) return null;
  const c = item.matches[0].overall_confidence;
  return typeof c === "number" ? c : null;
}

export function getEntityMatch(item: ProcessingRequest, key: string) {
  if (!item.matches || item.matches.length === 0) return null;
  return item.matches[0].entity_matches?.[key] ?? null;
}

export function getAttachmentCount(item: ProcessingRequest): number {
  return item.incoming_data?.attachments?.length ?? 0;
}

export function hasAudioAttachment(item: ProcessingRequest): boolean {
  return (item.incoming_data?.attachments ?? []).some(
    (a) => a.content_type?.startsWith("audio/") || /\.(wav|mp3|ogg|m4a)$/i.test(a.filename),
  );
}

/* ─── Date ─── */

export function formatDataBreve(iso: string | null): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const dayMonth = d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
    const time = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
    return `${dayMonth} ${time}`;
  } catch { return iso; }
}

export function formatDataEstesa(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

/* ─── Celle ─── */

const TRATTINO = <span className="text-muted">{"—"}</span>;

export function prioritaCell(item: ProcessingRequest) {
  const p = getPriorita(item);
  if (!p || p === "bassa") return null;
  const color = p === "alta" ? "var(--indaco-danger)" : "var(--indaco-warning)";
  const label = p === "alta" ? "Alta" : "Media";
  return (
    <span className="material-symbols-outlined" title={`Priorità: ${label}`}
      style={{ fontSize: 16, color, display: "block", textAlign: "center" }}>
      priority_high
    </span>
  );
}

export function causaleCell(item: ProcessingRequest) {
  const c = getCausale(item);
  if (!c) return TRATTINO;
  const cfg = CAUSALE_BADGE[c] ?? { bg: "var(--indaco-gray-100)", color: "var(--indaco-gray-700)", icon: "label", short: c, full: c };
  return (
    <span title={cfg.full}
      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 28, height: 28, borderRadius: "50%", background: cfg.bg, color: cfg.color }}>
      <span className="material-symbols-outlined" style={{ fontSize: 17 }}>{cfg.icon}</span>
    </span>
  );
}

export function riferimentoCell(item: ProcessingRequest) {
  const raw = getNumeroRiferimento(item);
  if (!raw) return TRATTINO;
  const refs = raw.split(",").map((r) => r.trim()).filter(Boolean);
  const MAX = 3;
  const visible = refs.slice(0, MAX);
  const extra = refs.length - MAX;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {visible.map((r, i) => (
        <code key={i} style={{ fontSize: 10, fontFamily: "monospace", color: "var(--indaco-gray-500)",
          lineHeight: 1.4, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {r}
        </code>
      ))}
      {extra > 0 && <span style={{ fontSize: 10, color: "var(--indaco-gray-400)" }}>+{extra}</span>}
    </div>
  );
}

export function impiantoCell(item: ProcessingRequest) {
  const m = getEntityMatch(item, "impianto");
  if (!m?.best?.entity) return TRATTINO;
  const pk = stripPkPrefix(m.best.entity.pk);
  const descTipo = m.best.entity.fields?.desc_tipo ? String(m.best.entity.fields.desc_tipo) : "";
  const descrizione = m.best.entity.fields?.descrizione ? String(m.best.entity.fields.descrizione) : "";
  const dettaglio = descTipo && descTipo.toLowerCase() !== "non definito" ? descTipo : descrizione;
  const tooltip = dettaglio ? `${pk} — ${dettaglio}` : pk;
  return (
    <code title={tooltip} style={{ fontFamily: "monospace", fontSize: "var(--font-size-sm)",
                                   fontWeight: "var(--font-weight-semibold)", whiteSpace: "nowrap" }}>
      {pk}
    </code>
  );
}

/** Edificio: codice canonico + descrizione, la descrizione sotto in piccolo
 *  perche' e' quella che l'operatore riconosce a colpo d'occhio. */
export function edificioCell(item: ProcessingRequest) {
  const m = getEntityMatch(item, "edificio");
  if (!m?.best?.entity) return TRATTINO;
  const pk = stripPkPrefix(m.best.entity.pk);
  const descrizione = m.best.entity.fields?.descrizione ? String(m.best.entity.fields.descrizione) : "";
  const indirizzo = m.best.entity.fields?.indirizzo ? String(m.best.entity.fields.indirizzo) : "";
  const tooltip = [pk, descrizione, indirizzo].filter(Boolean).join(" — ");
  return (
    <div title={tooltip} style={{ minWidth: 0 }}>
      <code style={{ fontFamily: "monospace", fontSize: "var(--font-size-xs)",
                     fontWeight: "var(--font-weight-semibold)", display: "block" }}>{pk}</code>
      {descrizione && (
        <span className="truncate" style={{ display: "block", fontSize: 10, color: "var(--indaco-gray-500)" }}>
          {descrizione}
        </span>
      )}
    </div>
  );
}

/** Cliente: il match dedicato quando c'e', altrimenti il `codice_cliente`
 *  dell'edificio — che nei dati veri e' spesso l'unica traccia del cliente,
 *  perche' l'edificio si risolve e il cliente no. */
export function clienteCell(item: ProcessingRequest) {
  const m = getEntityMatch(item, "cliente");
  const entity = m?.best?.entity;
  const codiceDaEdificio = getEntityMatch(item, "edificio")?.best?.entity?.fields?.codice_cliente;
  const codice = stripPkPrefix(entity?.pk) || (codiceDaEdificio ? String(codiceDaEdificio) : "");
  const ragsoc = entity?.fields?.ragsoc ? String(entity.fields.ragsoc) : "";
  if (!codice && !ragsoc) return TRATTINO;
  const tooltip = [codice, ragsoc].filter(Boolean).join(" — ");
  return (
    <div title={tooltip} style={{ minWidth: 0 }}>
      {codice && (
        <code style={{ fontFamily: "monospace", fontSize: "var(--font-size-xs)",
                       fontWeight: "var(--font-weight-semibold)", display: "block" }}>{codice}</code>
      )}
      {ragsoc && (
        <span className="truncate" style={{ display: "block", fontSize: 10, color: "var(--indaco-gray-500)" }}>
          {ragsoc}
        </span>
      )}
    </div>
  );
}

export function confidenzaCell(item: ProcessingRequest) {
  const confidence = getOverallConfidence(item);
  if (confidence == null) return TRATTINO;
  return (
    <span style={{
      fontWeight: 700, fontSize: 13, whiteSpace: "nowrap",
      color: confidence >= 0.8 ? "var(--confidence-high)"
           : confidence >= 0.5 ? "var(--confidence-medium)"
           : "var(--confidence-low)",
    }}>
      {Math.round(confidence * 100)}%
    </span>
  );
}

export function allegatiCell(item: ProcessingRequest) {
  const n = getAttachmentCount(item);
  if (n === 0) return TRATTINO;
  return (
    <span title={`${n} allegat${n === 1 ? "o" : "i"}`}
      style={{ display: "inline-flex", alignItems: "center", gap: 2, fontSize: "var(--font-size-sm)" }}>
      <span className="material-symbols-outlined" style={{ fontSize: 14, color: "var(--indaco-gray-500)" }}>attach_file</span>
      {n}
    </span>
  );
}

/* ─── Intestazione di colonna: ordinamento + popover di filtro ─── */

/** Chiude il popover di filtro al click fuori. Il `setTimeout` evita che lo
 *  stesso click che apre il popover lo richiuda subito. */
export function useColumnFilterPopover() {
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  useEffect(() => {
    if (!openFilter) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as HTMLElement;
      if (target.closest("[data-filter-popover]") || target.closest("[data-filter-toggle]")) return;
      setOpenFilter(null);
    }
    const timer = setTimeout(() => document.addEventListener("click", handleClick), 0);
    return () => { clearTimeout(timer); document.removeEventListener("click", handleClick); };
  }, [openFilter]);
  return { openFilter, setOpenFilter };
}

/** Il poco di stato che una lista deve prestare alle sue intestazioni: come
 *  e' ordinata, come si scrive un filtro nell'URL, quale popover e' aperto e
 *  gli estremi del filtro di confidenza (l'unico a due valori). */
export interface ListaColonneCtx {
  sort: string;
  setFilter: (updates: Record<string, string | null>) => void;
  setTextFilter: (key: string, value: string) => void;
  openFilter: string | null;
  setOpenFilter: (k: string | null) => void;
  confMin: string;
  confMax: string;
}

export interface ColHeaderProps {
  ctx: ListaColonneCtx;
  label: string;
  sortKey: string;
  filterKey?: string;
  filterType?: "text" | "select" | "range";
  filterValue?: string;
  options?: { value: string; label: string }[];
  style?: React.CSSProperties;
}

export function ColHeader({ ctx, label, sortKey, filterKey, filterType, filterValue, options, style }: ColHeaderProps) {
  const { sort, setFilter, setTextFilter, openFilter, setOpenFilter, confMin, confMax } = ctx;
  const isAsc = sort === `${sortKey}_asc`;
  const isDesc = sort === `${sortKey}_desc`;
  const isSortActive = isAsc || isDesc;
  const nextSort = isAsc ? `${sortKey}_desc` : isDesc ? null : `${sortKey}_asc`;
  const isFiltered = filterKey ? Boolean(filterValue) : false;
  const isOpen = openFilter === sortKey;

  return (
    <th style={{ position: "relative", ...style }}>
      {/* `thead th` e' `white-space: nowrap` + `overflow: hidden`: senza
          `minWidth: 0` sull'etichetta e `flexShrink: 0` sulle icone, una
          colonna stretta taglia l'intestazione a meta' parola invece di
          accorciarla ("Ultima attività" diventava "Ultima attivit"). Con i
          puntini di sospensione l'etichetta resta leggibile e le due icone
          di ordinamento e filtro non spariscono mai. */}
      <div style={{ display: "flex", alignItems: "center", gap: 2, userSelect: "none", minWidth: 0 }}>
        <span title={label || undefined}
          style={{ cursor: "pointer", flex: "1 1 auto", minWidth: 0,
                   overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          onClick={() => setFilter({ sort: nextSort })}>
          {label}
        </span>
        <span className={`material-symbols-outlined${isSortActive ? "" : " th-icon-hover"}`}
          onClick={() => setFilter({ sort: nextSort })}
          style={{ fontSize: 14, cursor: "pointer", padding: 2, flexShrink: 0,
                   color: "var(--indaco-brand-primary)", opacity: isSortActive ? 1 : 0.6 }}>
          {isAsc ? "arrow_upward" : isDesc ? "arrow_downward" : "swap_vert"}
        </span>
        {filterKey && (
          <span className={`material-symbols-outlined${isFiltered ? "" : " th-icon-hover"}`}
            data-filter-toggle onClick={() => setOpenFilter(isOpen ? null : sortKey)}
            style={{ fontSize: 14, cursor: "pointer", padding: 2, flexShrink: 0,
                     color: "var(--indaco-brand-primary)", opacity: isFiltered ? 1 : 0.6,
                     background: isFiltered ? "rgba(var(--indaco-brand-primary-rgb),0.1)" : "transparent",
                     borderRadius: 4 }}>
            filter_alt
          </span>
        )}
      </div>
      {isOpen && filterKey && (
        <div data-filter-popover
          style={{ position: "absolute", top: "100%", left: 0, zIndex: 100, minWidth: 180,
                   background: "var(--indaco-white)", border: "1px solid var(--indaco-gray-200)", borderRadius: "var(--border-radius-lg)",
                   boxShadow: "var(--shadow-lg)", padding: "8px 10px", marginTop: 4 }}>
          {filterType === "select" && options && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {options.map((o) => (
                <button key={o.value}
                  className={`btn btn-sm ${filterValue === o.value || (!filterValue && !o.value) ? "btn-primary" : "btn-ghost"}`}
                  style={{ justifyContent: "flex-start", fontSize: "var(--font-size-xs)", padding: "4px 8px" }}
                  onClick={() => { setFilter({ [filterKey]: o.value || null }); setOpenFilter(null); }}>
                  {o.label}
                </button>
              ))}
            </div>
          )}
          {filterType === "text" && (
            <input autoFocus type="text" className="form-input"
              style={{ fontSize: "var(--font-size-xs)", padding: "4px 8px", width: "100%" }}
              placeholder={`Filtra ${label.toLowerCase()}...`}
              defaultValue={filterValue}
              onChange={(e) => setTextFilter(filterKey, e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") setOpenFilter(null); }} />
          )}
          {filterType === "range" && (
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input type="text" inputMode="numeric" pattern="[0-9]*" className="form-input" placeholder="Min"
                style={{ fontSize: "var(--font-size-xs)", padding: "4px 6px", width: 60 }}
                value={confMin} onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ""); setFilter({ conf_min: v || null }); }} />
              <span className="text-muted text-xs">&ndash;</span>
              <input type="text" inputMode="numeric" pattern="[0-9]*" className="form-input" placeholder="Max"
                style={{ fontSize: "var(--font-size-xs)", padding: "4px 6px", width: 60 }}
                value={confMax} onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ""); setFilter({ conf_max: v || null }); }} />
            </div>
          )}
          {filterValue && (
            <button className="btn btn-ghost btn-sm" style={{ marginTop: 6, fontSize: "var(--font-size-xs)", width: "100%", color: "var(--indaco-danger)" }}
              onClick={() => {
                if (filterType === "range") setFilter({ conf_min: null, conf_max: null });
                else setFilter({ [filterKey]: null });
                setOpenFilter(null);
              }}>
              <span className="material-symbols-outlined" style={{ fontSize: 13, marginRight: 4 }}>close</span>Rimuovi filtro
            </button>
          )}
        </div>
      )}
    </th>
  );
}
